// src/pages/Contacts.js
import { useState, useMemo, useCallback, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as XLSX from 'xlsx'
import api from '../lib/api'
import MergeModal from '../components/MergeModal'

// ── Constants ─────────────────────────────────────────────────────────────

const CONTACT_TYPES = ['Investor', 'GP', 'Partner Advisor', 'Service Provider', 'RGA Team']

const TYPE_BADGE = {
  'Investor':        'badge-blue',
  'GP':              'badge-green',
  'Partner Advisor': 'badge-purple',
  'Service Provider':'badge-gray',
  'RGA Team':        'badge-yellow',
}
const INT_TYPE_BADGE = {
  'Email': 'badge-blue', 'Meeting': 'badge-purple',
  'Call': 'badge-green', 'Note': 'badge-gray', 'Event': 'badge-yellow',
}

const TAG_COLORS = ['badge-blue','badge-green','badge-purple','badge-yellow','badge-red','badge-gray']
function tagColor(t) { return TAG_COLORS[Math.abs([...t].reduce((a,c) => a + c.charCodeAt(0), 0)) % TAG_COLORS.length] }

const DSC_RANGES = [
  { label: '< 7 days',    test: d => d != null && d < 7 },
  { label: '7-30 days',   test: d => d != null && d >= 7 && d <= 30 },
  { label: '30-60 days',  test: d => d != null && d > 30 && d <= 60 },
  { label: '60+ days',    test: d => d != null && d > 60 },
]

const SORTABLE_COLS = {
  name: { label: 'Name', fn: (a, b) => (a.name || '').localeCompare(b.name || '') },
  firm: { label: 'Firm', fn: (a, b) => (a.primary_firm?.name || '').localeCompare(b.primary_firm?.name || '') },
  title: { label: 'Role / Title', fn: (a, b) => (a.title_role || '').localeCompare(b.title_role || '') },
  email: { label: 'Email', fn: (a, b) => ((a.email ?? [])[0] || '').localeCompare((b.email ?? [])[0] || '') },
  last_interaction: { label: 'Last Interaction', fn: (a, b) => (a.last_interaction || '').localeCompare(b.last_interaction || '') },
  days_since: { label: 'Days Since', fn: (a, b) => (a.days_since_contact ?? 9999) - (b.days_since_contact ?? 9999) },
  notes: { label: 'Notes', fn: (a, b) => (a.notes || '').localeCompare(b.notes || '') },
}

// ── Helpers ───────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return null
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function todayStr() { return new Date().toISOString().slice(0, 10) }

// ── Shared UI ─────────────────────────────────────────────────────────────

function Modal({ title, onClose, width = 560, zIndex = 1000, children }) {
  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex, padding: 16,
    }}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border-light)',
        borderRadius: 10, width: '100%', maxWidth: width,
        maxHeight: '92vh', overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
      }}>
        <div style={{
          padding: '16px 24px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, background: 'var(--surface)', zIndex: 1,
        }}>
          <h2 style={{ fontSize: 15, fontWeight: 600 }}>{title}</h2>
          <button className="btn-icon" onClick={onClose} style={{ fontSize: 16 }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  )
}

function ErrorBanner({ msg }) {
  if (!msg) return null
  return (
    <div style={{
      background: 'var(--danger-muted)', border: '1px solid rgba(239,68,68,0.25)',
      borderRadius: 6, padding: '10px 14px', fontSize: 13, color: '#f87171',
    }}>{msg}</div>
  )
}

function TrashIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
      <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
    </svg>
  )
}

function DeleteConfirmModal({ name, onCancel, onConfirm, deleting }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1200, padding: 16,
    }}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border-light)',
        borderRadius: 10, width: '100%', maxWidth: 420,
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)', padding: '24px',
      }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>Delete {name}?</h2>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 24, lineHeight: 1.5 }}>
          Are you sure you want to delete <strong style={{ color: 'var(--text-primary)' }}>{name}</strong>?
          This action cannot be undone.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button className="btn btn-secondary" onClick={onCancel} disabled={deleting}>Cancel</button>
          <button className="btn btn-danger" onClick={onConfirm} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
        letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>{children || '—'}</div>
    </div>
  )
}

function SortHeader({ col, sortCol, sortDir, onSort, children }) {
  const active = sortCol === col
  return (
    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
      onClick={() => onSort(col)}>
      {children} {active ? (sortDir === 'asc' ? '▲' : '▼') : <span style={{ opacity: 0.25 }}>▲</span>}
    </th>
  )
}

// ── Tag Input Component ──────────────────────────────────────────────────

function TagInput({ tags, onChange, suggestions = [] }) {
  const [input, setInput] = useState('')
  const [showSuggestions, setShowSuggestions] = useState(false)

  const filteredSuggestions = useMemo(() => {
    if (!input.trim()) return []
    const q = input.toLowerCase()
    return suggestions
      .filter(s => s.toLowerCase().includes(q) && !tags.includes(s))
      .slice(0, 8)
  }, [input, suggestions, tags])

  function addTag(t) {
    const tag = t.trim()
    if (tag && !tags.includes(tag)) onChange([...tags, tag])
    setInput('')
    setShowSuggestions(false)
  }

  function removeTag(t) { onChange(tags.filter(x => x !== t)) }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && input.trim()) {
      e.preventDefault()
      addTag(input)
    }
    if (e.key === 'Backspace' && !input && tags.length) {
      removeTag(tags[tags.length - 1])
    }
  }

  return (
    <div>
      <div style={{ position: 'relative' }}>
        <input
          className="input"
          value={input}
          onChange={e => { setInput(e.target.value); setShowSuggestions(true) }}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          placeholder={tags.length ? 'Add another tag…' : 'Type a tag and press Enter'}
        />
        {showSuggestions && filteredSuggestions.length > 0 && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
            background: 'var(--surface)', border: '1px solid var(--border-light)',
            borderRadius: 6, marginTop: 2, maxHeight: 180, overflow: 'auto',
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
          }}>
            {filteredSuggestions.map(s => (
              <div key={s}
                onMouseDown={() => addTag(s)}
                style={{
                  padding: '7px 12px', fontSize: 12, cursor: 'pointer',
                  color: 'var(--text-primary)',
                }}
                onMouseEnter={e => e.target.style.background = 'var(--accent-muted)'}
                onMouseLeave={e => e.target.style.background = 'transparent'}>
                {s}
              </div>
            ))}
          </div>
        )}
      </div>
      {tags.length > 0 && (
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 6 }}>
          {tags.map(t => (
            <span key={t} className={`badge ${tagColor(t)}`} style={{ fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {t}
              <span onClick={() => removeTag(t)}
                style={{ cursor: 'pointer', opacity: 0.7, fontWeight: 700, fontSize: 13, lineHeight: 1 }}>
                ×
              </span>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Multi-Select Filter Dropdown ─────────────────────────────────────────

function MultiSelectFilter({ label, options, selected, onToggle, badgeMap }) {
  const [open, setOpen] = useState(false)
  return (
    <div style={{ position: 'relative' }}>
      <button className="btn btn-secondary btn-sm"
        onClick={() => setOpen(o => !o)}
        style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
        {label} {selected.length > 0 && (
          <span style={{
            background: 'var(--accent)', color: '#fff', borderRadius: 8,
            padding: '0 5px', fontSize: 10, fontWeight: 700, lineHeight: '16px',
          }}>{selected.length}</span>
        )}
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 10,
          background: 'var(--surface)', border: '1px solid var(--border-light)',
          borderRadius: 6, marginTop: 4, minWidth: 180, maxHeight: 260, overflow: 'auto',
          boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        }}
          onMouseLeave={() => setOpen(false)}>
          {options.map(o => {
            const checked = selected.includes(o)
            return (
              <label key={o} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px',
                cursor: 'pointer', fontSize: 12,
                background: checked ? 'var(--accent-muted)' : 'transparent',
              }}>
                <input type="checkbox" checked={checked} onChange={() => onToggle(o)}
                  style={{ accentColor: 'var(--accent)' }} />
                {badgeMap
                  ? <span className={`badge ${badgeMap[o] ?? 'badge-gray'}`} style={{ fontSize: 10 }}>{o}</span>
                  : <span className={`badge ${tagColor(o)}`} style={{ fontSize: 10 }}>{o}</span>
                }
              </label>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Pagination bar ────────────────────────────────────────────────────────

function PaginationBar({ page, totalPages, pageStart, pageSize, totalCount, onPage }) {
  const from = totalCount === 0 ? 0 : pageStart + 1
  const to   = Math.min(pageStart + pageSize, totalCount)

  function pageNums() {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1)
    const pages = []
    pages.push(1)
    if (page > 4) pages.push('…')
    const start = Math.max(2, page - 2)
    const end   = Math.min(totalPages - 1, page + 2)
    for (let i = start; i <= end; i++) pages.push(i)
    if (page < totalPages - 3) pages.push('…')
    pages.push(totalPages)
    return pages
  }

  const btnBase = {
    minWidth: 30, height: 28, padding: '0 7px', borderRadius: 4,
    border: '1px solid var(--border)', cursor: 'pointer', fontSize: 12,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, flexWrap: 'wrap', gap: 8 }}>
      <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
        Showing {from}–{to} of {totalCount} records
      </span>
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <button
          onClick={() => onPage(p => Math.max(1, p - 1))}
          disabled={page <= 1}
          style={{ ...btnBase, background: 'var(--surface-raised)', color: 'var(--text-secondary)' }}
        >‹ Prev</button>

        {pageNums().map((n, i) =>
          n === '…'
            ? <span key={`e${i}`} style={{ fontSize: 12, color: 'var(--text-muted)', padding: '0 4px' }}>…</span>
            : <button
                key={n}
                onClick={() => onPage(n)}
                style={{
                  ...btnBase,
                  background: n === page ? 'var(--accent)' : 'var(--surface-raised)',
                  color: n === page ? '#fff' : 'var(--text-secondary)',
                  fontWeight: n === page ? 700 : 400,
                  borderColor: n === page ? 'var(--accent)' : 'var(--border)',
                }}
              >{n}</button>
        )}

        <button
          onClick={() => onPage(p => Math.min(totalPages, p + 1))}
          disabled={page >= totalPages}
          style={{ ...btnBase, background: 'var(--surface-raised)', color: 'var(--text-secondary)' }}
        >Next ›</button>
      </div>
    </div>
  )
}

// ── Export helpers ────────────────────────────────────────────────────────

function exportContacts(rows, users, format) {
  const resolveOwners = (ids) => {
    if (!ids || !ids.length) return ''
    return ids.map(id => {
      const u = users.find(u => u.id === id)
      return u ? u.name : id.slice(0, 8) + '...'
    }).join(', ')
  }

  const data = rows.map(c => ({
    'Name':               c.name ?? '',
    'Title/Role':         c.title_role ?? '',
    'Primary Firm':       c.primary_firm?.name ?? '',
    'Email(s)':           (c.email ?? []).join(', '),
    'Phone(s)':           (c.phone ?? []).join(', '),
    'Contact Type':       (c.contact_type ?? []).join(', '),
    'Tags':               (c.tags ?? []).join(', '),
    'Introduced By':      '',
    'Relationship Owner': resolveOwners(c.relationship_owner ?? []),
    'Last Interaction':   c.last_interaction ? new Date(c.last_interaction).toLocaleDateString() : '',
    'Days Since Contact': c.days_since_contact ?? '',
  }))

  const ws = XLSX.utils.json_to_sheet(data)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Contacts')

  const filename = `RGA_Contacts_${todayStr()}.${format === 'xlsx' ? 'xlsx' : 'csv'}`
  if (format === 'xlsx') {
    XLSX.writeFile(wb, filename)
  } else {
    XLSX.writeFile(wb, filename, { bookType: 'csv' })
  }
}

// ── Contact form (create + edit) ──────────────────────────────────────────

function ContactForm({ initial = {}, firms, allContacts, users, tagSuggestions, onSubmit, onCancel, submitting, error }) {
  const [form, setForm] = useState({
    name:               initial.name              ?? '',
    primary_firm_id:    initial.primary_firm_id   ?? '',
    title_role:         initial.title_role         ?? '',
    contact_type:       initial.contact_type       ?? [],
    email:             (initial.email             ?? []).join(', '),
    phone:             (initial.phone             ?? []).join(', '),
    introduced_by:      initial.introduced_by      ?? '',
    relationship_owner: initial.relationship_owner ?? [],
    tags:               initial.tags               ?? [],
  })
  const [err, setErr] = useState(null)

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); setErr(null) }

  function toggleType(t) {
    setForm(p => ({
      ...p,
      contact_type: p.contact_type.includes(t)
        ? p.contact_type.filter(x => x !== t)
        : [...p.contact_type, t],
    }))
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.name.trim()) return setErr('Name is required.')
    const payload = { name: form.name.trim() }
    if (form.primary_firm_id)    payload.primary_firm_id    = form.primary_firm_id
    if (form.title_role)         payload.title_role          = form.title_role.trim()
    if (form.contact_type.length) payload.contact_type       = form.contact_type
    if (form.email.trim())       payload.email               = form.email.split(',').map(s => s.trim()).filter(Boolean)
    if (form.phone.trim())       payload.phone               = form.phone.split(',').map(s => s.trim()).filter(Boolean)
    if (form.introduced_by)      payload.introduced_by       = form.introduced_by
    payload.relationship_owner = form.relationship_owner
    if (form.tags.length)        payload.tags                = form.tags
    try { await onSubmit(payload) }
    catch (err2) { setErr(err2.response?.data?.error || err2.message) }
  }

  return (
    <form onSubmit={handleSubmit} style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <ErrorBanner msg={err || error} />

      {/* Name */}
      <div className="input-group">
        <label className="input-label">Name *</label>
        <input className="input" value={form.name} onChange={e => set('name', e.target.value)}
          placeholder="Full name" autoFocus />
      </div>

      {/* Primary Firm + Title */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="input-group">
          <label className="input-label">Primary Firm</label>
          <select className="select" value={form.primary_firm_id} onChange={e => set('primary_firm_id', e.target.value)}>
            <option value="">None</option>
            {(firms ?? []).map(f => <option key={f.firm_id} value={f.firm_id}>{f.name}</option>)}
          </select>
        </div>
        <div className="input-group">
          <label className="input-label">Title / Role</label>
          <input className="input" value={form.title_role} onChange={e => set('title_role', e.target.value)}
            placeholder="e.g. Head of PE" />
        </div>
      </div>

      {/* Contact Type */}
      <div className="input-group">
        <label className="input-label">Contact Type</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
          {CONTACT_TYPES.map(t => {
            const checked = form.contact_type.includes(t)
            return (
              <label key={t} style={{
                display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer',
                fontSize: 12, padding: '5px 10px', borderRadius: 5,
                border: `1px solid ${checked ? 'var(--accent)' : 'var(--border)'}`,
                background: checked ? 'var(--accent-muted)' : 'transparent',
                color: checked ? 'var(--text-primary)' : 'var(--text-secondary)',
                transition: 'all 0.12s',
              }}>
                <input type="checkbox" checked={checked} onChange={() => toggleType(t)}
                  style={{ accentColor: 'var(--accent)', cursor: 'pointer' }} />
                {t}
              </label>
            )
          })}
        </div>
      </div>

      {/* Email + Phone */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="input-group">
          <label className="input-label">Email(s)</label>
          <input className="input" value={form.email} onChange={e => set('email', e.target.value)}
            placeholder="a@b.com, c@d.com" />
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Comma-separated</div>
        </div>
        <div className="input-group">
          <label className="input-label">Phone(s)</label>
          <input className="input" value={form.phone} onChange={e => set('phone', e.target.value)}
            placeholder="+1 555 0100" />
        </div>
      </div>

      {/* Introduced By */}
      <div className="input-group">
        <label className="input-label">Introduced By</label>
        <select className="select" value={form.introduced_by} onChange={e => set('introduced_by', e.target.value)}>
          <option value="">None</option>
          {(allContacts ?? []).map(c => <option key={c.contact_id} value={c.contact_id}>{c.name}</option>)}
        </select>
      </div>

      {/* Relationship Owner — multi-select badges */}
      <div className="input-group">
        <label className="input-label">Relationship Owner</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
          {(users ?? []).map(u => {
            const on = form.relationship_owner.includes(u.id)
            return (
              <button key={u.id} type="button"
                onClick={() => setForm(p => ({
                  ...p,
                  relationship_owner: on
                    ? p.relationship_owner.filter(id => id !== u.id)
                    : [...p.relationship_owner, u.id],
                }))}
                style={{
                  fontSize: 12, padding: '5px 12px', borderRadius: 12, cursor: 'pointer',
                  border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
                  background: on ? 'var(--accent)' : 'transparent',
                  color: on ? '#fff' : 'var(--text-secondary)',
                  transition: 'all 0.12s',
                }}>{u.name}</button>
            )
          })}
        </div>
      </div>

      {/* Tags */}
      <div className="input-group">
        <label className="input-label">Tags</label>
        <TagInput tags={form.tags} onChange={v => set('tags', v)} suggestions={tagSuggestions ?? []} />
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 8,
        borderTop: '1px solid var(--border)', marginTop: 4 }}>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? 'Saving...' : 'Save'}
        </button>
      </div>
    </form>
  )
}

// ── Contact detail modal ──────────────────────────────────────────────────

function ContactDetailModal({ contact, onClose, onUpdated, firms, allContacts, users, tagSuggestions }) {
  const [editMode, setEditMode] = useState(false)
  const qc = useQueryClient()

  // Activity timeline for this contact
  const { data: activityData, isLoading: activityLoading } = useQuery({
    queryKey: ['interactions', { contact_id: contact.contact_id }],
    queryFn: () => api.get('/api/interactions', { params: { contact_id: contact.contact_id } }).then(r => r.data.data ?? []),
  })
  const activity = useMemo(() => activityData ?? [], [activityData])

  // Unique mandates from interactions
  const linkedMandates = useMemo(() => {
    const seen = new Set()
    return activity
      .filter(i => i.mandate && !seen.has(i.mandate.mandate_id) && seen.add(i.mandate.mandate_id))
      .map(i => i.mandate)
  }, [activity])

  const updateMutation = useMutation({
    mutationFn: payload => api.patch(`/api/contacts/${contact.contact_id}`, payload).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] })
      qc.invalidateQueries({ queryKey: ['contactTags'] })
      setEditMode(false)
      onUpdated?.()
    },
  })

  const resolvedOwnerName = (contact.relationship_owner ?? []).map(id => {
    const u = (users ?? []).find(u => u.id === id)
    return u ? u.name : id.slice(0, 8) + '...'
  }).join(', ') || null

  if (editMode) {
    return (
      <Modal title={`Edit - ${contact.name}`} onClose={onClose} width={600}>
        <ContactForm
          initial={contact}
          firms={firms}
          allContacts={(allContacts ?? []).filter(c => c.contact_id !== contact.contact_id)}
          users={users}
          tagSuggestions={tagSuggestions}
          onSubmit={updateMutation.mutateAsync}
          onCancel={() => setEditMode(false)}
          submitting={updateMutation.isPending}
          error={updateMutation.error?.response?.data?.error}
        />
      </Modal>
    )
  }

  return (
    <Modal title={contact.name} onClose={onClose} width={600}>
      <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* Header row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{contact.name}</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 3 }}>
              {contact.title_role ?? ''}
              {contact.primary_firm && <span> &middot; {contact.primary_firm.name}</span>}
            </div>
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 8 }}>
              {(contact.contact_type ?? []).map(t => (
                <span key={t} className={`badge ${TYPE_BADGE[t] ?? 'badge-gray'}`}>{t}</span>
              ))}
            </div>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={() => setEditMode(true)}>Edit</button>
        </div>

        <div className="divider" />

        {/* Detail grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
          <Field label="Email">
            {(contact.email ?? []).map((e, i) => (
              <div key={i}><a href={`mailto:${e}`} style={{ color: 'var(--accent)' }}>{e}</a></div>
            ))}
          </Field>
          <Field label="Phone">
            {(contact.phone ?? []).join(', ') || null}
          </Field>
          <Field label="Last Interaction">{formatDate(contact.last_interaction)}</Field>
          <Field label="Days Since Contact">
            {contact.days_since_contact != null
              ? <span style={{ color: contact.days_since_contact > 30 ? 'var(--danger)' : 'var(--text-primary)' }}>
                  {contact.days_since_contact}d
                </span>
              : null}
          </Field>
          <Field label="Introduced By">
            {allContacts?.find(c => c.contact_id === contact.introduced_by)?.name ?? (contact.introduced_by ? contact.introduced_by.slice(0, 8) + '...' : null)}
          </Field>
          <Field label="Relationship Owner">{resolvedOwnerName}</Field>
        </div>

        {/* Tags section — always shown so users can see it's editable */}
        <div className="divider" />
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 8 }}>
            Tags
          </div>
          {(contact.tags ?? []).length > 0 ? (
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              {contact.tags.map(t => (
                <span key={t} className={`badge ${tagColor(t)}`} style={{ fontSize: 11 }}>{t}</span>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No tags</div>
          )}
        </div>

        {/* Associated mandates */}
        {linkedMandates.length > 0 && (
          <>
            <div className="divider" />
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 8 }}>
                Associated Mandates
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {linkedMandates.map(m => (
                  <span key={m.mandate_id} className="badge badge-blue">{m.mandate_name}</span>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Activity timeline */}
        <div className="divider" />
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 10 }}>
            Activity Timeline
          </div>
          {activityLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
              <div className="spinner" />
            </div>
          ) : activity.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '12px 0' }}>
              No interactions recorded yet.
            </div>
          ) : (
            <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
              {activity.map((i, idx) => (
                <div key={i.interaction_id} style={{
                  padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10,
                  borderBottom: idx < activity.length - 1 ? '1px solid var(--border)' : 'none',
                }}>
                  <span className={`badge ${INT_TYPE_BADGE[i.type] ?? 'badge-gray'}`} style={{ flexShrink: 0 }}>
                    {i.type}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden',
                      textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {i.subject_title || '(no subject)'}
                    </div>
                    {i.mandate && (
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 1 }}>
                        {i.mandate.mandate_name}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0 }}>
                    {formatDate(i.date)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </Modal>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────

export default function Contacts() {
  const [search, setSearch]             = useState('')
  const [typeFilter, setTypeFilter]     = useState([])
  const [tagFilter, setTagFilter]       = useState([])
  const [ownerFilter, setOwnerFilter]   = useState('')
  const [dscFilter, setDscFilter]       = useState('')
  const [sortCol, setSortCol]           = useState('name')
  const [sortDir, setSortDir]           = useState('asc')
  const [page, setPage]                 = useState(1)
  const [pageSize, setPageSize]         = useState(20)
  const [selected, setSelected]         = useState(null)
  const [createOpen, setCreate]         = useState(false)
  const [toDelete, setToDelete]         = useState(null)
  // ── merge / bulk-delete state ────────────────────────────────────────────
  const [checkedIds, setCheckedIds]     = useState(new Set())
  const [mergeOpen, setMergeOpen]       = useState(false)
  const [mergePrimaryId, setMergePrimaryId] = useState(null)
  const [mergeError, setMergeError]     = useState(null)
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)
  const [bulkDeleting, setBulkDeleting]     = useState(false)
  const [bulkDeleteResult, setBulkDeleteResult] = useState(null) // { succeeded, failed }
  const qc = useQueryClient()

  const { data: contactsData, isLoading } = useQuery({
    queryKey: ['contacts'],
    queryFn: () => api.get('/api/contacts').then(r => r.data.data ?? []),
  })
  const { data: firmsData } = useQuery({
    queryKey: ['firms'],
    queryFn: () => api.get('/api/firms').then(r => r.data.data ?? []),
  })
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get('/api/users').then(r => r.data.data ?? []),
    staleTime: 5 * 60 * 1000,
  })
  const { data: tagSuggestions } = useQuery({
    queryKey: ['contactTags'],
    queryFn: () => api.get('/api/contacts/tags').then(r => r.data.data ?? []),
    staleTime: 60 * 1000,
  })

  const contacts = useMemo(() => contactsData ?? [], [contactsData])
  const firms    = firmsData    ?? []
  const users    = useMemo(() => usersData ?? [], [usersData])

  // Collect all unique tags from contacts for filter
  const allTags = useMemo(() => {
    const s = new Set()
    for (const c of contacts) { for (const t of (c.tags ?? [])) s.add(t) }
    return Array.from(s).sort()
  }, [contacts])

  const ownerName = useCallback((contact) => {
    const ids = Array.isArray(contact.relationship_owner) ? contact.relationship_owner : []
    if (!ids.length) return '—'
    const names = ids.map(id => {
      const u = users.find(u => u.id === id)
      return u ? u.name : id.slice(0, 8) + '...'
    })
    if (names.length <= 2) return names.join(', ')
    return names.slice(0, 2).join(', ') + ` +${names.length - 2} more`
  }, [users])

  function toggleTypeFilter(t) { setTypeFilter(p => p.includes(t) ? p.filter(x => x !== t) : [...p, t]) }
  function toggleTagFilter(t) { setTagFilter(p => p.includes(t) ? p.filter(x => x !== t) : [...p, t]) }

  const hasFilters = Boolean(search || typeFilter.length || tagFilter.length || ownerFilter || dscFilter)
  const activeFilterCount = (typeFilter.length ? 1 : 0) + (tagFilter.length ? 1 : 0) + (ownerFilter ? 1 : 0) + (dscFilter ? 1 : 0)

  function clearAllFilters() {
    setSearch(''); setTypeFilter([]); setTagFilter([]); setOwnerFilter(''); setDscFilter('')
    setPage(1)
  }

  function handleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(col); setSortDir('asc') }
  }

  const filtered = useMemo(() => {
    let list = contacts.filter(c => {
      if (search && !c.name.toLowerCase().includes(search.toLowerCase())) return false
      if (typeFilter.length && !typeFilter.some(t => c.contact_type?.includes(t))) return false
      if (tagFilter.length && !tagFilter.some(t => (c.tags ?? []).includes(t))) return false
      if (ownerFilter && !(c.relationship_owner ?? []).includes(ownerFilter)) return false
      if (dscFilter) {
        const range = DSC_RANGES.find(r => r.label === dscFilter)
        if (range && !range.test(c.days_since_contact)) return false
      }
      return true
    })

    const sortFn = SORTABLE_COLS[sortCol]?.fn
    if (sortFn) {
      list = [...list].sort((a, b) => sortDir === 'asc' ? sortFn(a, b) : sortFn(b, a))
    }
    return list
  }, [contacts, search, typeFilter, tagFilter, ownerFilter, dscFilter, sortCol, sortDir])

  // Reset to page 1 whenever filters or sort change
  useEffect(() => { setPage(1) }, [search, typeFilter, tagFilter, ownerFilter, dscFilter, sortCol, sortDir])

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize))
  const safePage   = Math.min(page, totalPages)
  const pageStart  = (safePage - 1) * pageSize
  const paginated  = filtered.slice(pageStart, pageStart + pageSize)

  const createMutation = useMutation({
    mutationFn: payload => api.post('/api/contacts', payload).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] })
      qc.invalidateQueries({ queryKey: ['contactTags'] })
      setCreate(false)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: id => api.delete(`/api/contacts/${id}`).then(r => r.data),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['contacts'] })
      qc.invalidateQueries({ queryKey: ['contactTags'] })
      setToDelete(null)
      setSelected(prev => prev?.contact_id === id ? null : prev)
    },
  })

  const mergeMutation = useMutation({
    mutationFn: ({ primary_id, merge_ids }) =>
      api.post('/api/contacts/merge', { primary_id, merge_ids }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] })
      qc.invalidateQueries({ queryKey: ['contactTags'] })
      setCheckedIds(new Set())
      setMergeOpen(false)
      setMergeError(null)
    },
    onError: err => setMergeError(err.response?.data?.error || err.message),
  })

  // ── checkbox helpers ─────────────────────────────────────────────────────
  function toggleCheck(id, e) {
    e.stopPropagation()
    setCheckedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }
  function toggleCheckAll() {
    const pageIds = paginated.map(c => c.contact_id)
    const allPageChecked = pageIds.every(id => checkedIds.has(id))
    if (allPageChecked) {
      setCheckedIds(prev => { const n = new Set(prev); pageIds.forEach(id => n.delete(id)); return n })
    } else {
      setCheckedIds(prev => { const n = new Set(prev); pageIds.forEach(id => n.add(id)); return n })
    }
  }

  const checkedRecords = useMemo(
    () => contacts.filter(c => checkedIds.has(c.contact_id)),
    [contacts, checkedIds]
  )

  function openMerge() {
    const sorted = [...checkedRecords].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    )
    setMergePrimaryId(sorted[0]?.contact_id ?? null)
    setMergeError(null)
    setMergeOpen(true)
  }

  async function bulkDeleteContacts() {
    setBulkDeleting(true)
    setBulkDeleteResult(null)
    let succeeded = 0, failed = 0
    for (const id of checkedIds) {
      try {
        await api.delete(`/api/contacts/${id}`)
        succeeded++
      } catch {
        failed++
      }
    }
    await qc.invalidateQueries({ queryKey: ['contacts'] })
    await qc.invalidateQueries({ queryKey: ['contactTags'] })
    setCheckedIds(new Set())
    setBulkDeleting(false)
    setBulkDeleteOpen(false)
    if (failed > 0) setBulkDeleteResult({ succeeded, failed })
  }

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-text">
          <h1>Contacts</h1>
          <p style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isLoading ? 'Loading...' : `${contacts.length} contact${contacts.length !== 1 ? 's' : ''}`}
            {checkedIds.size > 0 && (
              <button
                onClick={() => setCheckedIds(new Set())}
                style={{
                  background: 'none', border: 'none', padding: 0,
                  fontSize: 12, color: 'var(--text-secondary)',
                  cursor: 'pointer', textDecoration: 'underline',
                }}
              >
                Clear {checkedIds.size} selected
              </button>
            )}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {checkedIds.size >= 2 && (
            <button className="btn btn-secondary" onClick={openMerge}
              style={{ fontWeight: 600 }}>
              Merge Selected ({checkedIds.size})
            </button>
          )}
          {checkedIds.size >= 1 && (
            <button className="btn btn-danger" onClick={() => { setBulkDeleteResult(null); setBulkDeleteOpen(true) }}>
              Delete Selected ({checkedIds.size})
            </button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={() => exportContacts(filtered, users, 'csv')}>Export CSV</button>
          <button className="btn btn-secondary btn-sm" onClick={() => exportContacts(filtered, users, 'xlsx')}>Export Excel</button>
          <button className="btn btn-primary" onClick={() => setCreate(true)}>+ New Contact</button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input className="input" style={{ maxWidth: 280 }} placeholder="Search by name..."
          value={search} onChange={e => setSearch(e.target.value)} />

        <MultiSelectFilter label="Contact Type" options={CONTACT_TYPES}
          selected={typeFilter} onToggle={toggleTypeFilter} badgeMap={TYPE_BADGE} />

        {allTags.length > 0 && (
          <MultiSelectFilter label="Tags" options={allTags}
            selected={tagFilter} onToggle={toggleTagFilter} />
        )}

        <select className="select" style={{ maxWidth: 180, fontSize: 12 }} value={ownerFilter}
          onChange={e => setOwnerFilter(e.target.value)}>
          <option value="">All owners</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>

        <select className="select" style={{ maxWidth: 160, fontSize: 12 }} value={dscFilter}
          onChange={e => setDscFilter(e.target.value)}>
          <option value="">Days since...</option>
          {DSC_RANGES.map(r => <option key={r.label} value={r.label}>{r.label}</option>)}
        </select>

        {hasFilters && (
          <button className="btn btn-ghost btn-sm" onClick={clearAllFilters}
            style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            Clear all{activeFilterCount > 0 && (
              <span style={{
                background: 'var(--accent)', color: '#fff', borderRadius: 8,
                padding: '0 5px', fontSize: 10, fontWeight: 700, lineHeight: '16px',
              }}>{activeFilterCount}</span>
            )}
          </button>
        )}

        {filtered.length !== contacts.length && (
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            Showing {filtered.length} of {contacts.length}
          </span>
        )}
      </div>

      {/* Per-page toggle */}
      {!isLoading && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 6, marginBottom: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
          <span>Per page:</span>
          {[20, 50].map(n => (
            <button key={n} onClick={() => { setPageSize(n); setPage(1) }} style={{
              padding: '3px 10px', borderRadius: 4, fontSize: 12, cursor: 'pointer',
              border: '1px solid var(--border)',
              background: pageSize === n ? 'var(--accent)' : 'var(--surface-raised)',
              color: pageSize === n ? '#fff' : 'var(--text-secondary)',
              fontWeight: pageSize === n ? 600 : 400,
            }}>{n}</button>
          ))}
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div className="spinner spinner-lg" />
        </div>
      ) : (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th style={{ width: 36, paddingRight: 0 }} onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    style={{ accentColor: 'var(--accent)', cursor: 'pointer' }}
                    checked={paginated.length > 0 && paginated.every(c => checkedIds.has(c.contact_id))}
                    ref={el => { if (el) { const pids = paginated.map(c => c.contact_id); el.indeterminate = pids.some(id => checkedIds.has(id)) && !pids.every(id => checkedIds.has(id)) } }}
                    onChange={toggleCheckAll}
                    title="Select all visible"
                  />
                </th>
                <SortHeader col="name" sortCol={sortCol} sortDir={sortDir} onSort={handleSort}>Name</SortHeader>
                <SortHeader col="firm" sortCol={sortCol} sortDir={sortDir} onSort={handleSort}>Firm</SortHeader>
                <SortHeader col="title" sortCol={sortCol} sortDir={sortDir} onSort={handleSort}>Role / Title</SortHeader>
                <SortHeader col="email" sortCol={sortCol} sortDir={sortDir} onSort={handleSort}>Email</SortHeader>
                <th>Type</th>
                <th>Tags</th>
                <SortHeader col="last_interaction" sortCol={sortCol} sortDir={sortDir} onSort={handleSort}>Last Interaction</SortHeader>
                <SortHeader col="days_since" sortCol={sortCol} sortDir={sortDir} onSort={handleSort}>Days Since</SortHeader>
                <th>Owner</th>
                <SortHeader col="notes" sortCol={sortCol} sortDir={sortDir} onSort={handleSort}>Notes</SortHeader>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={12}>
                  <div className="empty-state" style={{ padding: '40px 20px' }}>
                    <h3>{hasFilters ? 'No contacts match your filters' : 'No contacts yet'}</h3>
                    <p>{!hasFilters && 'Create your first contact to get started.'}</p>
                  </div>
                </td></tr>
              ) : (
                paginated.map(c => {
                  const dsc = c.days_since_contact
                  return (
                    <tr
                      key={c.contact_id}
                      style={{
                        cursor: 'pointer',
                        background: checkedIds.has(c.contact_id) ? 'var(--accent-muted)' : undefined,
                      }}
                      onClick={() => setSelected(c)}
                    >
                      <td style={{ width: 36, paddingRight: 0 }} onClick={e => toggleCheck(c.contact_id, e)}>
                        <input
                          type="checkbox"
                          checked={checkedIds.has(c.contact_id)}
                          onChange={e => toggleCheck(c.contact_id, e)}
                          onClick={e => e.stopPropagation()}
                          style={{ accentColor: 'var(--accent)', cursor: 'pointer' }}
                        />
                      </td>
                      <td style={{ fontWeight: 500 }}>{c.name}</td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                        {c.primary_firm?.name ?? '—'}
                      </td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                        {c.title_role ?? '—'}
                      </td>
                      <td onClick={e => e.stopPropagation()} style={{ fontSize: 12 }}>
                        {(c.email ?? []).length === 0
                          ? <span style={{ color: 'var(--text-muted)' }}>—</span>
                          : <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                              <a href={`mailto:${c.email[0]}`}
                                style={{ color: 'var(--accent)', textDecoration: 'none',
                                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                  maxWidth: 180, display: 'block' }}
                                title={c.email[0]}>
                                {c.email[0]}
                              </a>
                              {c.email.length > 1 && (
                                <span className="badge badge-gray" style={{ fontSize: 10, flexShrink: 0 }}>
                                  +{c.email.length - 1}
                                </span>
                              )}
                            </div>
                        }
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {(c.contact_type ?? []).map(t => (
                            <span key={t} className={`badge ${TYPE_BADGE[t] ?? 'badge-gray'}`}
                              style={{ fontSize: 10 }}>{t}</span>
                          ))}
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {(c.tags ?? []).slice(0, 3).map(t => (
                            <span key={t} className={`badge ${tagColor(t)}`} style={{ fontSize: 10 }}>{t}</span>
                          ))}
                          {(c.tags ?? []).length > 3 && (
                            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>+{c.tags.length - 3}</span>
                          )}
                        </div>
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        {formatDate(c.last_interaction) ?? '—'}
                      </td>
                      <td>
                        {dsc != null
                          ? <span style={{
                              fontSize: 12, fontWeight: dsc > 30 ? 600 : 400,
                              color: dsc > 30 ? 'var(--danger)' : 'var(--text-primary)',
                            }}>{dsc}d</span>
                          : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
                        }
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        {ownerName(c)}
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text-secondary)', maxWidth: 180 }}>
                        {c.notes
                          ? <span title={c.notes}>{c.notes.length > 50 ? c.notes.slice(0, 50) + '...' : c.notes}</span>
                          : <span style={{ color: 'var(--text-muted)' }}>—</span>
                        }
                      </td>
                      <td onClick={e => e.stopPropagation()} style={{ width: 36, paddingRight: 8 }}>
                        <button
                          onClick={() => setToDelete(c)}
                          style={{
                            background: 'transparent', border: 'none', cursor: 'pointer',
                            color: 'var(--text-muted)', padding: '3px 5px', borderRadius: 4,
                            display: 'inline-flex', alignItems: 'center',
                          }}
                          onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                          onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                          title="Delete contact"
                        ><TrashIcon /></button>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination bar */}
      {!isLoading && filtered.length > 0 && (
        <PaginationBar
          page={safePage}
          totalPages={totalPages}
          pageStart={pageStart}
          pageSize={pageSize}
          totalCount={filtered.length}
          onPage={setPage}
        />
      )}

      {/* Create modal */}
      {createOpen && (
        <Modal title="New Contact" onClose={() => setCreate(false)}>
          <ContactForm
            firms={firms}
            allContacts={contacts}
            users={users}
            tagSuggestions={tagSuggestions ?? []}
            onSubmit={createMutation.mutateAsync}
            onCancel={() => setCreate(false)}
            submitting={createMutation.isPending}
            error={createMutation.error?.response?.data?.error}
          />
        </Modal>
      )}

      {/* Detail modal */}
      {selected && (
        <ContactDetailModal
          contact={selected}
          onClose={() => setSelected(null)}
          onUpdated={() => setSelected(null)}
          firms={firms}
          allContacts={contacts}
          users={users}
          tagSuggestions={tagSuggestions ?? []}
        />
      )}

      {/* Delete confirmation */}
      {toDelete && (
        <DeleteConfirmModal
          name={toDelete.name}
          onCancel={() => setToDelete(null)}
          onConfirm={() => deleteMutation.mutate(toDelete.contact_id)}
          deleting={deleteMutation.isPending}
        />
      )}

      {/* Merge modal */}
      {mergeOpen && checkedRecords.length >= 2 && (
        <MergeModal
          records={checkedRecords}
          entityType="contact"
          primaryId={mergePrimaryId}
          onPrimaryChange={setMergePrimaryId}
          onConfirm={() => mergeMutation.mutate({
            primary_id: mergePrimaryId,
            merge_ids:  checkedRecords.map(c => c.contact_id).filter(id => id !== mergePrimaryId),
          })}
          onCancel={() => { setMergeOpen(false); setMergeError(null) }}
          merging={mergeMutation.isPending}
          error={mergeError}
        />
      )}

      {/* Bulk-delete confirmation modal */}
      {bulkDeleteOpen && (
        <div
          onClick={e => e.target === e.currentTarget && !bulkDeleting && setBulkDeleteOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1200, padding: 16,
          }}
        >
          <div style={{
            background: 'var(--surface)', border: '1px solid var(--border-light)',
            borderRadius: 10, width: '100%', maxWidth: 460,
            boxShadow: '0 20px 60px rgba(0,0,0,0.6)', padding: '24px',
          }}>
            <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>
              Delete {checkedIds.size} Contact{checkedIds.size !== 1 ? 's' : ''}?
            </h2>

            {/* Name list — up to 10 */}
            <ul style={{ margin: '0 0 14px', padding: '0 0 0 18px', fontSize: 13, color: 'var(--text-primary)' }}>
              {checkedRecords.slice(0, 10).map(c => (
                <li key={c.contact_id} style={{ marginBottom: 2 }}>{c.name}</li>
              ))}
              {checkedRecords.length > 10 && (
                <li style={{ color: 'var(--text-secondary)' }}>
                  …and {checkedRecords.length - 10} more
                </li>
              )}
            </ul>

            {/* Warning */}
            <div style={{
              background: 'var(--danger-muted)', border: '1px solid rgba(239,68,68,0.25)',
              borderRadius: 6, padding: '10px 14px', fontSize: 12,
              color: '#f87171', marginBottom: 20, lineHeight: 1.5,
            }}>
              This action cannot be undone. Associated interactions and pipeline
              entries linked solely to these contacts may be affected.
            </div>

            {/* Partial-failure result */}
            {bulkDeleteResult && (
              <div style={{
                background: 'var(--danger-muted)', borderRadius: 6,
                padding: '8px 12px', fontSize: 12, color: '#f87171', marginBottom: 14,
              }}>
                {bulkDeleteResult.succeeded} deleted, {bulkDeleteResult.failed} failed.
                The failed records may have dependent data — delete them individually.
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button className="btn btn-secondary" onClick={() => setBulkDeleteOpen(false)} disabled={bulkDeleting}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={bulkDeleteContacts} disabled={bulkDeleting}>
                {bulkDeleting ? 'Deleting…' : `Delete ${checkedIds.size} Contact${checkedIds.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
